import React from "react";
import ContextMenu from "./ContextMenu";
import { MenuItem, MenuList, Paper } from "@mui/material";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";

const SituationalRulesContextMenu = (props) => {
  const { t } = useTranslation();
  const {updateFunction,fieldRulesArray,treeUpdate,setSelectedRule,selectedRule ,manualClick} = props

  const handleNewFieldRule = () => {
    try {
      // Input validation
      if (!fieldRulesArray || !Array.isArray(fieldRulesArray)) {
        console.error('Invalid fieldRulesArray:', fieldRulesArray);
        return;
      }

      if (!updateFunction || typeof updateFunction !== 'function') {
        console.error('updateFunction is not available or not a function');
        return;
      }

      if (!treeUpdate || typeof treeUpdate !== 'function') {
        console.error('treeUpdate is not available or not a function');
        return;
      }

      if (!setSelectedRule || typeof setSelectedRule !== 'function') {
        console.error('setSelectedRule is not available or not a function');
        return;
      }

      // Get translation with fallback
      const ruleTypeLabel = t('LBL_SELECT_VALIDATION_TYPE') || 'Select Validation Type';
      
      // Ensure manualClick ref exists
      if (manualClick && manualClick.current !== undefined) {
        manualClick.current = true;
      }

      // Create deep copy with error handling
      let treeData;
      try {
        treeData = [...cloneDeep(fieldRulesArray)];
      } catch (cloneError) {
        console.error('Error cloning fieldRulesArray:', cloneError);
        // Fallback to simple spread if cloneDeep fails
        treeData = [...fieldRulesArray];
      }

      // Create new node with validation
      const newNode = {
        ruleType: ruleTypeLabel,
        conditionalField: '',
        selectedField: [],
      };

      // Validate new node structure
      if (!newNode.ruleType || newNode.selectedField === undefined) {
        console.error('Failed to create valid new node:', newNode);
        return;
      }

      // Add new node to array
      treeData.push(newNode);
      
      // Calculate new index safely
      const newIndex = Math.max(0, treeData.length - 1);
      const newSelectedKey = `sfr-${newIndex}`;

      // Execute updates with error handling
      try {
        updateFunction(treeData);
      } catch (updateError) {
        console.error('Error in updateFunction:', updateError);
        return;
      }

      try {
        setSelectedRule([newSelectedKey]);
      } catch (selectionError) {
        console.error('Error setting selected rule:', selectionError);
        // Continue execution as this is not critical
      }

      try {
        treeUpdate(treeData);
      } catch (treeUpdateError) {
        console.error('Error in treeUpdate:', treeUpdateError);
        // Continue execution as the main update already succeeded
      }

    } catch (error) {
      console.error('Unexpected error in handleNewFieldRule:', error);
      // Reset manual click flag on error
      if (manualClick && manualClick.current !== undefined) {
        manualClick.current = false;
      }
    }
  }

  const handleDeleteFieldRule = () => {
    try {
      // Input validation
      if (!fieldRulesArray || !Array.isArray(fieldRulesArray)) {
        console.error('Invalid fieldRulesArray:', fieldRulesArray);
        return;
      }

      if (!selectedRule || !Array.isArray(selectedRule) || selectedRule.length === 0) {
        console.error('No rule selected for deletion:', selectedRule);
        return;
      }

      if (!updateFunction || typeof updateFunction !== 'function') {
        console.error('updateFunction is not available or not a function');
        return;
      }

      if (!treeUpdate || typeof treeUpdate !== 'function') {
        console.error('treeUpdate is not available or not a function');
        return;
      }

      if (!setSelectedRule || typeof setSelectedRule !== 'function') {
        console.error('setSelectedRule is not available or not a function');
        return;
      }

      // Ensure manualClick ref exists
      if (manualClick && manualClick.current !== undefined) {
        manualClick.current = true;
      }

      // Parse and validate selected index
      let index;
      try {
        const selectedKey = selectedRule[0];
        if (!selectedKey || typeof selectedKey !== 'string') {
          console.error('Invalid selected rule key:', selectedKey);
          return;
        }

        const indexStr = selectedKey.split('-')[1];
        index = Number(indexStr);
        
        if (isNaN(index) || index < 0 || index >= fieldRulesArray.length) {
          console.error('Invalid index for deletion:', index, 'Array length:', fieldRulesArray.length);
          return;
        }
      } catch (parseError) {
        console.error('Error parsing selected rule index:', parseError);
        return;
      }

      // Create deep copy with error handling
      let treeData;
      try {
        treeData = [...cloneDeep(fieldRulesArray)];
      } catch (cloneError) {
        console.error('Error cloning fieldRulesArray:', cloneError);
        // Fallback to simple spread if cloneDeep fails
        treeData = [...fieldRulesArray];
      }

      // Validate array bounds before deletion
      if (index >= treeData.length) {
        console.error('Index out of bounds for deletion:', index, 'Array length:', treeData.length);
        return;
      }

      // Remove the selected item
      treeData.splice(index, 1);

      // Execute updates with error handling
      try {
        updateFunction(treeData);
      } catch (updateError) {
        console.error('Error in updateFunction:', updateError);
        return;
      }

      try {
        treeUpdate(treeData);
      } catch (treeUpdateError) {
        console.error('Error in treeUpdate:', treeUpdateError);
        // Continue execution as the main update already succeeded
      }

      // Update selection logic with proper error handling
      try {
        if (treeData.length === 0) {
          setSelectedRule([]);
        } else if (index >= treeData.length) {
          // If deleted item was the last one, select the new last item
          setSelectedRule([`sfr-${treeData.length - 1}`]);
        } else {
          // Keep the same index if possible, otherwise select the previous item
          const newIndex = Math.min(index, treeData.length - 1);
          setSelectedRule([`sfr-${newIndex}`]);
        }
      } catch (selectionError) {
        console.error('Error setting selected rule after deletion:', selectionError);
        // Set empty selection as fallback
        try {
          setSelectedRule([]);
        } catch (fallbackError) {
          console.error('Failed to set fallback selection:', fallbackError);
        }
      }

    } catch (error) {
      console.error('Unexpected error in handleDeleteFieldRule:', error);
      // Reset manual click flag on error
      if (manualClick && manualClick.current !== undefined) {
        manualClick.current = false;
      }
    }
  }


  return (
    <ContextMenu targetId={props.targetId}>
      <Paper style={{ width: "19rem" }}>
        <MenuList vertical="true">
          <MenuItem onClick={handleNewFieldRule} name={t("MNU_NEW_SITUATION_FLD_RULE")}>
            <span className={"ITEM_PROPERTIES"}></span>
            <span className={"margin-left-8"}>
              {t("MNU_NEW_SITUATION_FLD_RULE")}
            </span>
          </MenuItem>
          <MenuItem disabled={selectedRule.length===0} onClick={handleDeleteFieldRule} name={t("MNU_DEL_SEL_FLD_RULE")}>
            <span className={"SYMBOL_DELETE"}></span>
            <span className={"margin-left-8"}>{t("MNU_DEL_SEL_FLD_RULE")}</span>
          </MenuItem>
        </MenuList>
      </Paper>
    </ContextMenu>
  );
};

export default SituationalRulesContextMenu;
